import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";

import { Section } from "@roqua/components/sections";

import { AccountNotVerified } from "@/components/AccountNotVerified";
import { ErrorPage } from "@/components/ErrorPage";
import { PendingResponses } from "@/components/PendingResponses";
import { SelfInitiables } from "@/components/SelfInitiables";
import { graphql } from "@/gql";
import { AuthenticatedByEnum } from "@/gql/graphql";
import { useClient } from "@/graphql/client";
import { useLoggedInSession } from "@/session";
import { toError } from "@/lib/utils";

const Query = graphql(/* GraphQL */ `
  query filloutDashboardPage($id: ID!) {
    respondent(id: $id) {
      responses(pending: true, first: 5) {
        totalCount
        ...PendingResponses_Fragment
      }

      selfInitiableMeasuredQuestionnaires {
        totalCount
        ...SelfInitiables_Fragment
      }
    }
  }
`);

export const Route = createFileRoute("/fillout/")({
  component: RouteComponent,
  errorComponent: ({ error }) => <ErrorPage message={toError(error).message} />,
});

function RouteComponent() {
  const client = useClient();
  const {
    userId,
    session: { authenticatedBy, verified },
  } = useLoggedInSession();
  const navigate = useNavigate();

  const notVerified = !!authenticatedBy.find((a) => a === AuthenticatedByEnum.Password);

  if (verified) {
    navigate({ to: "/portal", reloadDocument: true });
  }

  const {
    data: { respondent },
  } = useSuspenseQuery({
    queryKey: ["Fillout"],
    queryFn: () =>
      client.request(Query, {
        id: userId!,
      }),
  });

  const anythingToFillOut =
    respondent.responses.totalCount + respondent.selfInitiableMeasuredQuestionnaires.totalCount > 0;

  return (
    <main>
      {anythingToFillOut && (
        <Section variant="header">
          <h2 className="mb-4 text-xl">Vragenlijsten die je nu kunt invullen:</h2>
          <PendingResponses pendingResponses={respondent.responses} userId={userId} />
          <SelfInitiables
            selfInitiableMeasuredQuestionnaires={respondent.selfInitiableMeasuredQuestionnaires}
          />
        </Section>
      )}
      {!anythingToFillOut && (
        <Section variant="header">
          <h2 className="mb-4 text-xl">Op dit moment zijn er geen vragenlijsten in te vullen.</h2>
        </Section>
      )}

      {notVerified && <AccountNotVerified />}
    </main>
  );
}
